requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    if (window.stickyHeaderManagerLoaded) return;
    window.stickyHeaderManagerLoaded = true;

    themeBase.onFirstScroll(!themeBase.isDesktop || !window.themeSettings.fullWidthLogoEnabled ? true : false, () => {
      class StickyHeaderManager {
        constructor() {
          this.stickyClass = 'sticky-header__content--sticky';
          this.stickyHiddenClass = 'sticky-header__content--hidden';
          this.stickyWrapperClass = 'sticky-header-fixed';
          this.fixedMenuWrapperClass = 'sticky-header-fixed--fixed-mega-menu';
          this.fixedScrollOffset = 100;
          this.currentScroll = 0;
          this.desktopDirectionThreshold = 1;
          this.desktopRevealThreshold = 6;
          this.desktopHideThreshold = 10;
          this.mobileDirectionThreshold = 1;
          this.mobileRevealThreshold = 6;
          this.mobileHideThreshold = 10;
          this.params = {
            mobile: {
              limitSide: 'top',
              limitOffset: 300
            }
          };

          this.desktopTargets = [];
          this.mobileTarget = null;
          this.currentDesktopFixedHeight = 0;
          this.desktopScrollDirection = null;
          this.desktopDirectionAnchor = null;
          this.desktopHideableTargetsHidden = false;
          this.desktopStackDocumentBottom = 0;
          this.fullWidthLogoSectionDocumentBottom = 0;

          this.onResizeHandler = this.onResize.bind(this);
          this.onScrollHandler = this.onScroll.bind(this);
          this.onHeaderChangeHandler = this.onHeaderChange.bind(this);

          this.init();
        }

        init() {
          this.fixedFooter = document.querySelector('fixed-footer');
          this.refresh();
          this.onResize();
          this.onScroll();

          window.addEventListener('throttle30fpsResize', this.onResizeHandler);
          window.addEventListener('throttle30fpsScroll', this.onScrollHandler);
          window.addEventListener('HeaderPrimaryMaybeChanged', this.onHeaderChangeHandler);
          document.addEventListener('shopify:section:load', this.onHeaderChangeHandler);
          document.addEventListener('shopify:section:unload', this.onHeaderChangeHandler);
          document.addEventListener('shopify:section:reorder', this.onHeaderChangeHandler);
        }

        getWrapper() {
          return window.themeHeader?.getWrapper?.() || document.querySelector('.header-sections-wrapper');
        }

        getPrimarySection() {
          return window.themeHeader?.getPrimarySection?.() || document.querySelector('.header-sections-wrapper header-section[data-header-layout]');
        }

        getDesktopStackSections() {
          const sections = window.themeHeader?.getDesktopStackSections?.();

          if (sections && sections.length) return sections;

          const desktopSection = window.themeHeader?.getDesktopSection?.() || this.getPrimarySection();
          return desktopSection ? [desktopSection] : [];
        }

        getMobileSection() {
          return window.themeHeader?.getMobileSection?.() || this.getPrimarySection();
        }

        getActiveSection() {
          return window.themeHeader?.getActiveSection?.() || this.getPrimarySection();
        }

        setSharedStateClass(className, enabled) {
          this.wrapper?.classList.toggle(className, enabled);
          document.body.classList.toggle(className, enabled);
        }

        createSpacer(target, modifier, index) {
          if (!target?.parentNode) return null;

          const spacer = document.createElement('div');
          spacer.classList.add('sticky-header__spacer', `sticky-header__spacer--${modifier}`);
          spacer.dataset.stickySpacerIndex = `${modifier}-${index}`;
          target.parentNode.insertBefore(spacer, target);

          return spacer;
        }

        createTargetMeta(section, target, modifier, index, isPrimary = false) {
          if (!section || !target) return null;

          return {
            section,
            sectionRoot: window.themeHeader?.getSectionRoot?.(section) || section,
            target,
            spacer: this.createSpacer(target, modifier, index),
            hideWhenScrollDown: (section.querySelector('.header__first-line-wrapper')?.dataset?.hideWhenScrollDown ?? target?.dataset?.hideWhenScrollDown ?? 'true') === 'true',
            isPrimary,
            fixed: false,
            hidden: false,
            scrollDirection: null,
            directionAnchor: null,
            staticDocumentTop: 0,
            staticHeight: 0,
            staticSectionBottom: 0
          };
        }

        setMegaMenuStickyOffset(meta, value = '') {
          if (!meta) return;

          const section = meta.section || null;
          const sectionRoot = meta.sectionRoot || null;

          if (value === '' || value === null) {
            section?.style?.removeProperty('--sticky-mega-menu-spacing');
            sectionRoot?.style?.removeProperty('--sticky-mega-menu-spacing');
            return;
          }

          section?.style?.setProperty('--sticky-mega-menu-spacing', value);
          sectionRoot?.style?.setProperty('--sticky-mega-menu-spacing', value);
        }

        clearTarget(meta) {
          if (!meta) return;

          this.unfixTarget(meta);
          meta.spacer?.remove();
        }

        clearTargets(targets = []) {
          targets.forEach((meta) => this.clearTarget(meta));
        }

        buildDesktopTargets() {
          const primarySection = this.getPrimarySection();

          return this.getDesktopStackSections()
            .map((section, index) => this.createTargetMeta(
              section,
              section.querySelector('.sticky-header__content-desktop'),
              'd',
              index,
              section === primarySection
            ))
            .filter(Boolean);
        }

        buildMobileTarget() {
          const mobileSection = this.getMobileSection();
          return this.createTargetMeta(mobileSection, mobileSection?.querySelector('.sticky-header__content-mobile'), 'm', 0);
        }

        refresh() {
          this.wrapper = this.getWrapper();
          this.primarySection = this.getPrimarySection();
          this.desktopSections = this.getDesktopStackSections();
          this.mobileSection = this.getMobileSection();
          this.activeSection = this.getActiveSection();
          this.fullWidthLogo = this.wrapper?.querySelector('full-width-logo') || null;

          this.clearTargets(this.desktopTargets);
          this.clearTarget(this.mobileTarget);

          this.desktopTargets = this.buildDesktopTargets();
          this.mobileTarget = this.buildMobileTarget();

          this.updateFixedMenu();
        }

        onHeaderChange() {
          this.refresh();
          this.onResize();
          this.onScroll();
        }

        resetTarget(meta) {
          if (!meta) return;

          this.unfixTarget(meta);
          meta.scrollDirection = null;
          meta.directionAnchor = null;
          meta.spacer?.classList.remove('sticky-header__spacer--visible');
          meta.spacer?.removeAttribute('style');
        }

        resetDesktopTargets() {
          this.desktopTargets.forEach((meta) => this.resetTarget(meta));
          this.desktopScrollDirection = null;
          this.desktopDirectionAnchor = null;
          this.desktopHideableTargetsHidden = false;
        }

        resetMobileTarget() {
          this.resetTarget(this.mobileTarget);
        }

        resetStickyState() {
          this.resetDesktopTargets();
          this.resetMobileTarget();
          this.currentDesktopFixedHeight = 0;

          this.setSharedStateClass(this.stickyWrapperClass, false);
          document.documentElement.style.setProperty('--sticky-header-spacing', '0rem');
        }

        onResize() {
          if (themeBase.isFreezeScrollEnable) return;

          this.bp = themeBase.isDesktop ? 'desktop' : 'mobile';

          if (themeBase.isTablet) {
            this.resetStickyState();
            return;
          }

          if (themeBase.isDesktop) {
            this.resetMobileTarget();
            this.resetDesktopTargets();

            if (!this.desktopTargets.length) {
              this.resetStickyState();
              return;
            }

            this.measureDesktopLayoutMetrics();
          } else {
            this.resetDesktopTargets();
            this.currentDesktopFixedHeight = 0;

            if (!this.mobileTarget?.target) {
              this.resetStickyState();
              return;
            }
          }

          this.updateStickySpacing();
        }

        updateStickySpacing() {
          if (themeBase.isDesktop) {
            document.documentElement.style.setProperty('--sticky-header-spacing', `${this.currentDesktopFixedHeight / 10}rem`);
            return;
          }

          const mobileHeight = this.mobileTarget?.fixed && !this.mobileTarget?.hidden ? themeBounds.get(this.mobileTarget.target).height : 0;
          document.documentElement.style.setProperty('--sticky-header-spacing', `${mobileHeight / 10}rem`);
        }

        isFixedFooterVisible() {
          if (!this.fixedFooter || !this.fixedFooter.isConnected) return false;
          if (!this.fixedFooter.classList.contains('fixed-footer--fixed')) return false;
          if (window.getComputedStyle(this.fixedFooter).getPropertyValue('display') === 'none') return false;

          const mainContent = document.getElementById('MainContent');
          if (!mainContent) return !!this.fixedFooter.isVisible;

          return themeBounds.get(mainContent).bottom - 200 <= themeBounds.get(this.fixedFooter).bottom;
        }

        getDocumentBottom(element) {
          if (!element) return 0;

          return themeBounds.get(element).bottom + window.pageYOffset;
        }

        getDocumentTop(element) {
          if (!element) return 0;

          return themeBounds.get(element).top + window.pageYOffset;
        }

        measureDesktopLayoutMetrics() {
          this.desktopTargets.forEach((meta) => {
            if (!meta?.target) return;

            meta.staticDocumentTop = this.getDocumentTop(meta.target);
            meta.staticHeight = themeBounds.get(meta.target).height;
            meta.staticSectionBottom = this.getDocumentBottom(meta.sectionRoot || meta.section);
          });

          const lastDesktopTarget = this.desktopTargets[this.desktopTargets.length - 1] || null;
          this.desktopStackDocumentBottom = lastDesktopTarget?.staticSectionBottom || 0;

          const fullWidthLogoSectionRoot = this.fullWidthLogo?.closest?.('[id^="shopify-section-"]') || null;
          this.fullWidthLogoSectionDocumentBottom = fullWidthLogoSectionRoot ?
            this.getDocumentBottom(fullWidthLogoSectionRoot) :
            0;
        }

        getDesktopStickyHideGuardLimit() {
          if (
            window.themeSettings?.fullWidthLogoEnabled === true &&
            window.themeSettings?.fullWidthLogoSectionVisible === true &&
            this.fullWidthLogoSectionDocumentBottom > 0
          ) {
            return this.fullWidthLogoSectionDocumentBottom;
          }

          return this.desktopStackDocumentBottom;
        }

        canUseSticky() {
          return themeSettings.disableStickyHeader === undefined || !themeSettings.disableStickyHeader();
        }

        fixTarget(meta, topOffset, height) {
          if (!meta?.target) return;

          meta.target.style.setProperty('--sticky-content-top', `${topOffset}px`);
          meta.target.style.top = `${topOffset}px`;
          meta.spacer?.style.setProperty('height', `${height}px`);
          meta.spacer?.classList.add('sticky-header__spacer--visible');
          meta.target.classList.add(this.stickyClass);
          meta.fixed = true;
        }

        setTargetHidden(meta, hidden, hiddenOffset = null) {
          if (!meta?.target) return;

          if (hiddenOffset !== null) {
            meta.target.style.setProperty('--sticky-hidden-offset', `-${hiddenOffset}px`);
          } else {
            meta.target.style.removeProperty('--sticky-hidden-offset');
          }

          meta.target.classList.toggle(this.stickyHiddenClass, hidden);
          meta.hidden = hidden;
        }

        unfixTarget(meta) {
          if (!meta?.target) return;

          meta.target.style.removeProperty('--sticky-content-top');
          meta.target.style.removeProperty('top');
          meta.target.style.removeProperty('min-height');
          meta.target.style.removeProperty('--sticky-hidden-offset');
          meta.target.classList.remove(this.stickyClass);
          meta.target.classList.remove(this.stickyHiddenClass);
          meta.spacer?.removeAttribute('style');
          meta.spacer?.classList.remove('sticky-header__spacer--visible');
          this.setMegaMenuStickyOffset(meta);
          meta.fixed = false;
          meta.hidden = false;
          meta.scrollDirection = null;
          meta.directionAnchor = null;
        }

        onScrollDesktop() {
          const scrollDelta = window.pageYOffset - this.currentScroll;
          let activationFixedHeight = 0;
          let anyFixed = false;
          const scrollingUp = scrollDelta < (this.desktopDirectionThreshold * -1);
          const scrollingDown = scrollDelta > this.desktopDirectionThreshold;
          const isFixedFooter = this.isFixedFooterVisible();
          const hasVisibleDesktopMenus = this.desktopTargets.some((meta) => !!meta.section?.querySelectorAll('.menu .visible').length);
          const desktopStickyHideGuardLimit = this.getDesktopStickyHideGuardLimit();
          const withinDesktopStickyHideGuardRange = window.pageYOffset < desktopStickyHideGuardLimit;
          const fixedTargets = [];

          if (scrollingUp) {
            if (this.desktopScrollDirection !== 'up') {
              this.desktopScrollDirection = 'up';
              this.desktopDirectionAnchor = window.pageYOffset;
            }
          } else if (scrollingDown) {
            if (this.desktopScrollDirection !== 'down') {
              this.desktopScrollDirection = 'down';
              this.desktopDirectionAnchor = window.pageYOffset;
            }
          }

          this.desktopTargets.forEach((meta) => {
            const target = meta.target;
            const spacer = meta.spacer;

            if (!target || !spacer) return;

            const targetHeight = meta.staticHeight || themeBounds.get(target).height;
            const targetDocumentTop = meta.staticDocumentTop || this.getDocumentTop(target);
            const crossed = (window.pageYOffset + activationFixedHeight) >= (targetDocumentTop - 1);
            const stickyEligible = (
              crossed &&
              !isFixedFooter &&
              this.canUseSticky() &&
              !themeSettings.manuallyIgnoreStickyHeader
            );

            if (stickyEligible) {
              activationFixedHeight += targetHeight;
              fixedTargets.push({
                meta,
                targetHeight
              });
              anyFixed = true;
            } else {
              this.unfixTarget(meta);
            }
          });

          if (!anyFixed) {
            this.desktopScrollDirection = null;
            this.desktopDirectionAnchor = null;
            this.desktopHideableTargetsHidden = false;
            this.currentDesktopFixedHeight = 0;
            this.setSharedStateClass(this.stickyWrapperClass, false);
            this.updateStickySpacing();
            return;
          }

          const hideableFixedTargets = fixedTargets.filter(({
            meta
          }) => meta.hideWhenScrollDown);
          const shouldAlwaysShowHideableTargets = (
            withinDesktopStickyHideGuardRange ||
            hasVisibleDesktopMenus
          );

          if (!hideableFixedTargets.length || shouldAlwaysShowHideableTargets) {
            this.desktopHideableTargetsHidden = false;
          } else if (this.desktopHideableTargetsHidden) {
            const upwardTravel = this.desktopScrollDirection === 'up' ?
              (this.desktopDirectionAnchor ?? window.pageYOffset) - window.pageYOffset :
              0;

            if (upwardTravel >= this.desktopRevealThreshold) {
              this.desktopHideableTargetsHidden = false;
              this.desktopDirectionAnchor = window.pageYOffset;
            }
          } else {
            const downwardTravel = this.desktopScrollDirection === 'down' ?
              window.pageYOffset - (this.desktopDirectionAnchor ?? window.pageYOffset) :
              0;

            if (downwardTravel >= this.desktopHideThreshold) {
              this.desktopHideableTargetsHidden = true;
              this.desktopDirectionAnchor = window.pageYOffset;
            }
          }

          let visibleFixedHeight = 0;

          fixedTargets.forEach(({
            meta,
            targetHeight
          }) => {
            const hidden = meta.hideWhenScrollDown && this.desktopHideableTargetsHidden;
            const hiddenOffset = visibleFixedHeight + targetHeight;

            this.fixTarget(meta, visibleFixedHeight, targetHeight);
            this.setTargetHidden(meta, hidden, hidden ? hiddenOffset : null);

            if (!hidden) {
              visibleFixedHeight += targetHeight;
            }

            this.setMegaMenuStickyOffset(meta, `${visibleFixedHeight}px`);
          });

          this.currentDesktopFixedHeight = visibleFixedHeight;
          this.setSharedStateClass(this.stickyWrapperClass, visibleFixedHeight > 0);
          this.updateStickySpacing();
        }

        onScrollMobile() {
          const meta = this.mobileTarget;
          if (!meta?.target || !meta.spacer) return;

          const referenceElement = meta.fixed ? meta.spacer : meta.target;
          const referenceParams = themeBounds.get(referenceElement);
          const limitOffset = this.params.mobile.limitOffset || 0;
          const limit = (0 + limitOffset) * -1;
          const isFixedFooter = this.isFixedFooterVisible();
          const sectionHasVisibleMenus = !!meta.section?.querySelectorAll('.menu .visible').length;
          const shouldAlwaysShow = !meta.hideWhenScrollDown || sectionHasVisibleMenus;
          const scrollDelta = window.pageYOffset - this.currentScroll;
          const scrollingUp = scrollDelta < (this.mobileDirectionThreshold * -1);
          const scrollingDown = scrollDelta > this.mobileDirectionThreshold;

          if (scrollingUp) {
            if (meta.scrollDirection !== 'up') {
              meta.scrollDirection = 'up';
              meta.directionAnchor = window.pageYOffset;
            }
          } else if (scrollingDown) {
            if (meta.scrollDirection !== 'down') {
              meta.scrollDirection = 'down';
              meta.directionAnchor = window.pageYOffset;
            }
          }

          const stickyEligible = (
            referenceParams.top < limit &&
            !isFixedFooter &&
            this.canUseSticky() &&
            !themeSettings.manuallyIgnoreStickyHeader
          );

          if (!stickyEligible) {
            this.unfixTarget(meta);
            this.setSharedStateClass(this.stickyWrapperClass, false);
            this.updateStickySpacing();
            return;
          }

          const targetHeight = themeBounds.get(meta.target).height;
          const wasFixed = meta.fixed;

          this.fixTarget(meta, 0, targetHeight);

          if (!wasFixed) {
            meta.directionAnchor = window.pageYOffset;

            if (shouldAlwaysShow || scrollingUp) {
              this.setTargetHidden(meta, false);
            } else {
              this.setTargetHidden(meta, true);
            }
          } else if (shouldAlwaysShow) {
            this.setTargetHidden(meta, false);
          } else if (meta.hidden) {
            const upwardTravel = meta.scrollDirection === 'up' ?
              (meta.directionAnchor ?? window.pageYOffset) - window.pageYOffset :
              0;

            if (upwardTravel >= this.mobileRevealThreshold) {
              this.setTargetHidden(meta, false);
              meta.directionAnchor = window.pageYOffset;
            }
          } else {
            const downwardTravel = meta.scrollDirection === 'down' ?
              window.pageYOffset - (meta.directionAnchor ?? window.pageYOffset) :
              0;

            if (downwardTravel >= this.mobileHideThreshold) {
              this.setTargetHidden(meta, true);
              meta.directionAnchor = window.pageYOffset;
            }
          }

          this.setSharedStateClass(this.stickyWrapperClass, !!meta.fixed && !meta.hidden);
          this.updateStickySpacing();
        }

        onScroll() {
          if (themeBase.isFreezeScrollEnable) return;

          if (themeBase.isDesktop) {
            this.onScrollDesktop();
          } else {
            this.onScrollMobile();
          }

          this.currentScroll = window.pageYOffset;
        }

        updateFixedMenu() {
          this.fixedMenu = this.desktopTargets.some((meta) => meta.target?.querySelector('[data-menu-button]'));
          this.setSharedStateClass(this.fixedMenuWrapperClass, this.fixedMenu);
        }

        getStickyHeight() {
          if (themeBase.isDesktop) {
            return this.currentDesktopFixedHeight;
          }

          if (this.mobileTarget?.fixed) {
            return themeBounds.get(this.mobileTarget.target).height;
          }

          return 0;
        }
      }

      window.stickyHeaderManager = new StickyHeaderManager();
    });
  });
});